/**
 * CONTRACT FILE. Destination: src/app/registry/types.ts
 * Implemented by:
 *   - W1-11a (app-registry-keymap): src/app/registry/registry.ts exports `registry: Registry`;
 *     src/app/registry/loadModules.ts collects every `src/features/**\/*.module.ts(x)` default export
 *     (FeatureModule) with import.meta.glob — NO shared index file to edit: a package plugs in by adding
 *     its own module file; src/app/registry/keymap.ts exports `keymap: KeymapService`;
 *     src/app/registry/when.ts exports `parseWhen: ParseWhenFn`.
 *   - W1-16 (app-integration): src/app/services.ts exports `services: AppServices` (wired at startup by src/main.tsx).
 *
 * One command registry feeds menus, context menus, toolbar buttons, the command palette, the keymap,
 * the hint bar, the cheat sheet and checker fixes (DESIGN.md §8).
 *
 * Focus/hover regions: every panel root carries `data-region="lane|motion|curve|preview|lines|inspector|timing|
 * dock|problems|history|assets|raw|publish|library|settings"`; the KeyboardRouter derives `focus.<region>` and
 * `hover.<region>` atoms from the closest [data-region] of the focused and hovered elements; the dock is the only
 * container region, so `focus.dock` / `hover.dock` also hold inside its tabs. A panel nested in another panel (a
 * curve editor inside a motion column) reports only its own region: bindings that should also work there name both.
 */

import type { ComponentType, ReactNode } from 'react';
import type { Json, MessageRef, Unsubscribe } from '../../core/util/types';
import type { AssetId, EffectId, KeyId, LineId, NoteId, ProjectId, TrackId } from '../../core/model/types';
import type { BadgeProps } from '../../ui/types';
import type { TempoCandidate } from '../../core/audio-dsp/types';
import type { Engine } from '../../core/engine/types';
import type { Problem } from '../../core/check/types';
import type { CheckRule } from '../../core/check/types';
import type { ClipboardPayload } from '../../core/commands/types';
import type { DocStore, EditorStoreApi, Selection, WorkspaceId, ExperienceLevel } from '../../state/types';
import type { AudioEngine } from '../../audio/types';
import type { PersistClient } from '../../persist/types';
import type { I18n, MessageKey } from '../../i18n/types';
import type {
  FrameScheduler,
  PreviewOverlayProvider,
  SkinStore,
  TextureProvider,
  TimelineColumnDef,
  TimelineDecorationProvider,
  TimelineHostApi,
} from '../../render/types';
import type {
  AnalysisWorkerEvents,
  AnalysisWorkerSchema,
  AudioDspWorkerEvents,
  AudioDspWorkerSchema,
  IoWorkerEvents,
  IoWorkerSchema,
  RpcClient,
} from '../../workers/protocol';
import type { SettingsService, SettingPath } from '../settings/types';
import type { DialogId, DialogMap } from '../dialogs/types';
import type { CommandArgs } from './commands';

/** lucide-react export name (PascalCase), e.g. 'Play', 'Magnet', 'TriangleAlert'. No emoji anywhere. */
export type IconName = string;

export type CommandId = string;

export type CommandCategory = 'File' | 'Edit' | 'Select' | 'View' | 'Playback' | 'Notes' | 'Motion' | 'Lines' | 'Timing' | 'Tools' | 'Publish' | 'Help';

// ---------------------------------------------------------------------------------------------------------------
// Context ("when") and chords
// ---------------------------------------------------------------------------------------------------------------

/**
 * Built-in context atoms (boolean). `focus.<region>` / `hover.<region>` exist for every data-region value.
 * A WhenExpr combines atoms: `hover.lane && !textInput`, `sel.notes || sel.keys`, `workspace == chart`,
 * `level == full`, `preset == rpe`, `os == mac`.
 * Selection-dependent keys shared by the lane and the motion columns follow one convention so they never overlap:
 * note bindings use `sel.notes && !textInput` (notes and mixed selections: the lane acts on both kinds, e.g. A
 * mirrors notes and negates keys), key bindings `(sel.keys || sel.segments) && !sel.notes`.
 */
export const BUILTIN_WHEN_ATOMS = [
  'textInput',
  'dialog',
  'paletteOpen',
  'focus.lane',
  'focus.motion',
  'focus.curve',
  'focus.preview',
  'focus.lines',
  'focus.inspector',
  'focus.timing',
  'focus.dock',
  'focus.problems',
  'focus.history',
  'focus.assets',
  'focus.raw',
  'focus.publish',
  'focus.library',
  'focus.settings',
  'hover.lane',
  'hover.motion',
  'hover.curve',
  'hover.preview',
  'hover.lines',
  'hover.inspector',
  'hover.timing',
  'hover.dock',
  'hover.problems',
  'hover.history',
  'hover.assets',
  'hover.raw',
  'hover.publish',
  'hover.library',
  'hover.settings',
  'placing.hold',
  'placing.segment',
  'paste.ghost',
  'gesture',
  'playing',
  'sel.notes',
  'sel.keys',
  'sel.segments',
  'sel.lines',
  'sel.bpm',
  'sel.any',
  'sel.single',
  'sel.mixed',
  'column.focused',
  'project.open',
  'activeLine.uiMover',
  'activeLine.notesAllowed',
] as const;
export type BuiltinWhenAtom = (typeof BUILTIN_WHEN_ATOMS)[number];

/**
 * Any atom: a built-in or one registered by a module (FeatureModule.whenAtoms, e.g. 'testPlay', 'theater',
 * 'effects.selected'). parseWhen validates names against both sets.
 */
export type WhenAtom = string;

/** Parsed boolean expression over WhenAtoms and `name == value` comparisons (workspace, level, preset, tool, os). */
export type WhenExpr = string;

export interface WhenContext {
  has(atom: WhenAtom): boolean;
  readonly workspace: WorkspaceId | 'library' | 'settings';
  readonly level: ExperienceLevel;
  readonly preset: KeymapPresetId;
  readonly tool: string;
  readonly os: 'mac' | 'other';
}

export type ParseWhenFn = (
  expr: WhenExpr,
  knownAtoms: ReadonlySet<string>,
) => { readonly eval: (c: WhenContext) => boolean; readonly specificity: number } | { readonly error: string };

/**
 * Chord grammar (physical keys via KeyboardEvent.code; labels shown with the user's layout):
 *   [Ctrl+][Alt+][Shift+]<code>        e.g. 'Ctrl+KeyK', 'Shift+Digit1', 'Alt+Comma', 'Space', 'F5', 'Backslash'
 *   hold:<code>                        press-and-hold bindings (T/U hold-to-play), fire on down and up
 *   [Ctrl+][Alt+][Shift+]Wheel         mouse wheel gestures (Ctrl+Wheel zoom, Shift+Wheel fast scroll)
 *   char:<character>                   bind by produced character instead of position (e.g. 'char:?')
 * On macOS `Ctrl` in a chord means Cmd (Cmd+click toggles selection; Ctrl+click is a right-click there).
 * Ctrl+Alt chords are never used by presets (AltGr). Wheel chords: deltaY is normalised to pixels (deltaMode)
 * and scaled by settings.editing.scrollSensitivity; ctrlKey wheel (trackpad pinch) zooms; horizontal deltas pan
 * the lane; presets never rely on Shift+wheel on macOS (the OS turns it into horizontal scrolling).
 */
export type Chord = string;

export type KeymapPresetId = 'default' | 'rpe';

export interface KeyBinding {
  readonly chord: Chord;
  readonly command: CommandId;
  readonly args?: Json;
  readonly when?: WhenExpr;
  /** Alt-free variant replacement (chord without Alt) or null to unbind when altFree is on. */
  readonly altFree?: Chord | null;
  /**
   * macOS replacement, applied on macOS whenever declared (null = unbound on macOS; menus/palette remain).
   * Required when `chord` is in RESERVED_CHORDS_MAC.
   */
  readonly mac?: Chord | null;
  /**
   * User overrides only: deletes the preset binding with the same chord, command and when (keymap editor
   * "remove" / "unbind"). Preset definitions never set it.
   */
  readonly remove?: true;
}

/**
 * Chords the recorder REFUSES (browser/OS never deliver them to pages on at least one supported
 * browser/OS; the user's platform is Linux). Verified list from the judges + research.
 */
export const RESERVED_CHORDS: readonly Chord[] = [
  'Ctrl+KeyT', 'Ctrl+KeyN', 'Ctrl+KeyW', 'Ctrl+KeyQ',
  'Ctrl+Shift+KeyT', 'Ctrl+Shift+KeyN', 'Ctrl+Shift+KeyW', 'Ctrl+Shift+KeyQ',
  'Ctrl+Tab', 'Ctrl+Shift+Tab', 'Ctrl+PageUp', 'Ctrl+PageDown',
  'Ctrl+Digit1', 'Ctrl+Digit2', 'Ctrl+Digit3', 'Ctrl+Digit4', 'Ctrl+Digit5', 'Ctrl+Digit6', 'Ctrl+Digit7', 'Ctrl+Digit8', 'Ctrl+Digit9',
  'Alt+Digit1', 'Alt+Digit2', 'Alt+Digit3', 'Alt+Digit4', 'Alt+Digit5', 'Alt+Digit6', 'Alt+Digit7', 'Alt+Digit8', 'Alt+Digit9',
  'Alt+ArrowLeft', 'Alt+ArrowRight', 'Alt+Home', 'Alt+F4',
  'F11', 'F12',
  'Ctrl+Shift+KeyI', 'Ctrl+Shift+KeyJ', 'Ctrl+Shift+KeyC', 'Ctrl+Shift+KeyK', 'Ctrl+Shift+KeyE',
  'Ctrl+Shift+KeyP', 'Ctrl+Shift+KeyA', 'Ctrl+Shift+KeyM', 'Ctrl+Shift+KeyS', 'Ctrl+Shift+KeyR', 'Ctrl+Shift+Delete',
  'Ctrl+KeyR',
];

/**
 * macOS chords (`Ctrl` = Cmd) the recorder refuses on a Mac: quit/close/new tab/new window, hide (Cmd+H), minimise
 * (Cmd+M), browser preferences (Cmd+,), window cycling (Cmd+` / Cmd+Shift+`), history Back/Forward (Cmd+[ / Cmd+] /
 * Cmd+Left / Cmd+Right), tab switching (Cmd+Shift+[ / Cmd+Shift+]), address bar (Cmd+L). Every default that lands on
 * one declares a `mac` alternative.
 */
export const RESERVED_CHORDS_MAC: readonly Chord[] = [
  'Ctrl+KeyQ', 'Ctrl+KeyW', 'Ctrl+KeyT', 'Ctrl+KeyN', 'Ctrl+KeyH', 'Ctrl+KeyM', 'Ctrl+Comma', 'Ctrl+Backquote',
  'Ctrl+BracketLeft', 'Ctrl+BracketRight', 'Ctrl+KeyL', 'Ctrl+ArrowLeft', 'Ctrl+ArrowRight', 'Ctrl+Shift+KeyQ',
  'Ctrl+Shift+Backquote', 'Ctrl+Shift+BracketLeft', 'Ctrl+Shift+BracketRight',
];

/** Chords accepted but WARNED (browser menus/focus may take them on some browsers). Every default using one has a non-Alt alternative path. */
export const UNRELIABLE_CHORDS: readonly Chord[] = ['Alt+KeyD', 'Alt+KeyE', 'Alt+KeyF', 'F6', 'Ctrl+KeyL', 'Ctrl+KeyJ', 'Ctrl+KeyH'];

export interface KeymapService {
  readonly preset: KeymapPresetId;
  readonly altFree: boolean;
  setPreset(p: KeymapPresetId): void;
  setAltFree(on: boolean): void;
  /** Effective bindings (preset + overrides + altFree). */
  bindings(): readonly KeyBinding[];
  bindingsFor(command: CommandId): readonly Chord[];
  /** Human label in the user's keyboard layout, e.g. "Ctrl+K", "Shift+1", "Alt+," ("(physical)" on Firefox). */
  label(chord: Chord): string;
  /** Normalise a KeyboardEvent into a Chord (null for bare modifiers). */
  chordOf(e: KeyboardEvent): Chord | null;
  /** Resolve the most specific enabled binding for a chord in the current context. */
  resolve(chord: Chord, ctx: WhenContext): KeyBinding | null;
  /** Rebinding UI: conflicts in the same/overlapping context ('red') vs specificity-resolved overlaps ('yellow'). */
  conflicts(b: KeyBinding): ReadonlyArray<{ readonly with: KeyBinding; readonly level: 'red' | 'yellow' }>;
  /** Additions plus `remove: true` entries that delete preset bindings. Persisted in settings.keyboard.overrides. */
  setOverrides(o: readonly KeyBinding[]): void;
  exportJson(): string;
  importJson(json: string): { readonly ok: boolean; readonly errors: readonly string[] };
  /** P1: import RPE Hotkey.txt ("LEFTCTRL & J" syntax) through a function-name translation table. */
  importRpeHotkeys?(text: string): { readonly bindings: readonly KeyBinding[]; readonly unmapped: readonly string[] };
  /** 'mac' when Ctrl in chords means Cmd. */
  readonly os?: 'mac' | 'other';
  /**
   * Selection modifiers of a click: toggle = Ctrl+click (Cmd+click on macOS), shift, alt, and contextClick = right
   * button or Ctrl+click on macOS (a right-click there).
   */
  pointerModifiers?(e: MouseEvent): {
    readonly toggle: boolean;
    readonly shift: boolean;
    readonly alt: boolean;
    readonly contextClick: boolean;
    /** Bypass snapping during a drag: Alt, or Ctrl+Shift (Cmd+Shift) when the Alt-free variant is on. */
    readonly noSnap: boolean;
  };
  /**
   * Recorder rule for a chord: refused (RESERVED_CHORDS, RESERVED_CHORDS_MAC on macOS, Ctrl+Alt) or warned
   * (UNRELIABLE_CHORDS), with a translatable reason whose {chord} is the layout-aware label.
   */
  check?(
    chord: string,
  ):
    | { readonly status: 'ok' | 'invalid'; readonly chord: string }
    | { readonly status: 'reserved' | 'ctrlAlt' | 'unreliable'; readonly chord: string; readonly reason: MessageRef };
  /** Problems found in the user's overrides and the modules (reserved chord in an imported file, unknown command, bad condition). */
  issues?(): ReadonlyArray<{
    readonly level: 'error' | 'warning';
    readonly code: string;
    readonly text: MessageRef;
    /** The module whose binding it is (null / absent: the user's overrides). */
    readonly moduleId?: string | null;
    readonly binding?: KeyBinding;
  }>;
  subscribe(cb: () => void): Unsubscribe;
}

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

export type CommandSource = 'key' | 'palette' | 'menu' | 'button' | 'context' | 'fix' | 'gesture' | 'api';

/**
 * Palette argument prompts for parameterised commands ("Set beat snap...", "Go to line..."). The palette runs the
 * command with: `number` -> a number within [min, max]; `beat` -> a `BeatArg` ({ w, n, d }); `line` -> the LineId
 * (number); `text` -> the string; `choice` -> the option's `value`. Keymaps and menus may pass object args instead;
 * commands should accept both.
 */
export type ArgSpec =
  | { readonly kind: 'number'; readonly label: MessageRef; readonly min?: number; readonly max?: number }
  | { readonly kind: 'beat'; readonly label: MessageRef }
  | { readonly kind: 'line'; readonly label: MessageRef }
  | { readonly kind: 'text'; readonly label: MessageRef }
  | { readonly kind: 'choice'; readonly label: MessageRef; readonly options: ReadonlyArray<{ readonly value: Json; readonly label: MessageRef }> };

export interface CommandContext extends AppServices {
  readonly when: WhenContext;
  readonly source: CommandSource;
  /** Trigger event (pointer position for "at the mouse" commands). */
  readonly event?: KeyboardEvent | PointerEvent | WheelEvent;
  /** Beat under the pointer when a context menu opened (commands run from the menu). */
  readonly at?: number;
}

export interface CommandDef<A = unknown> {
  readonly id: CommandId;
  readonly title: MessageKey;
  readonly category: CommandCategory;
  readonly icon?: IconName;
  /** Search aliases incl. RPE and Chinese terms ("stick", "粘合", "MirrorY"). */
  readonly aliases?: readonly string[];
  /** Where it is ADVERTISED. Full-level commands stay in the palette in Guided with a "Full" tag and offer
   *  "Run once" or "Switch to Full". */
  readonly level: ExperienceLevel;
  readonly when?: WhenExpr;
  /**
   * true, or the reason it is disabled (shown in palette/menus/tooltips). `args`: the arguments of the run being
   * checked; absent when the command is listed (menus, palette, buttons) or run without arguments.
   */
  enabled?(ctx: CommandContext, args?: A): true | MessageRef;
  checked?(ctx: CommandContext): boolean;
  /** 'hold': run on keydown, `release` on keyup (auto-repeat ignored). */
  readonly kind?: 'press' | 'hold';
  /** Honour key auto-repeat (seek, nudge). */
  readonly repeat?: boolean;
  /** Hint bar ranking (higher first); omitted = not shown in the hint bar. */
  readonly hint?: number;
  /**
   * Hint bar sentence for the Guided level ('sentences' mode), with `{keys}` where the shortcut goes, e.g.
   * 'notes.placeTapHint': '{keys} places a Tap at the playhead'. Without it the bar uses the title key plus `Hint`
   * when the locale has it, else the shortcut and the title.
   */
  readonly hintSentence?: MessageKey;
  readonly args?: ArgSpec;
  /**
   * Title fragment for "command + arguments" rows (cheat sheet, keymap editor), e.g. "Move left: 1". Return null
   * to fall back to the shared formatter (`args.kind === 'choice'` option labels, else the raw values).
   */
  argsLabel?(args: Json): MessageRef | null;
  run(ctx: CommandContext, args: A): void | Promise<void>;
  release?(ctx: CommandContext): void;
  /** Menus this command appears in. */
  readonly menus?: readonly MenuPlacement[];
}

/** Registry menus. The time gutter of the timeline uses 'timing.context' (insert / remove time, markers). */
export type MenuId =
  | 'app.project'
  | 'app.edit'
  | 'app.view'
  | 'app.help'
  | 'lane.context'
  | 'motion.context'
  | 'curve.context'
  | 'preview.context'
  | 'lines.context'
  | 'inspector.toolbar'
  | 'library.card'
  | 'timing.context'
  | 'tools.menu';

export interface MenuPlacement {
  readonly menu: MenuId;
  readonly group: string;
  readonly order: number;
}

// ---------------------------------------------------------------------------------------------------------------
// UI contributions
// ---------------------------------------------------------------------------------------------------------------

/**
 * Layout slots per workspace (the shell arranges slots; modules fill them):
 *  chart:   tools (left rail) | timeline (centre: lanes + motion columns) | preview (right top) |
 *           dock (right bottom, tabbed: Lines, Problems, History, Assets, Raw) | inspector (far right) |
 *           motionDrawer (bottom, narrow)
 *  timing:  timing.left (tempo, offset, metronome) | timing.main (overview, alignment) | timing.bottom
 *  publish: publish.left (check, compatibility, stats) | publish.right (package, export)
 *  global:  topbar.left, topbar.center (transport), topbar.right (status chips), statusbar
 */
export type PanelSlot =
  | 'chart.tools'
  | 'chart.timeline'
  | 'chart.preview'
  | 'chart.dock'
  | 'chart.inspector'
  | 'chart.motionDrawer'
  | 'timing.left'
  | 'timing.main'
  | 'timing.bottom'
  | 'publish.left'
  | 'publish.right'
  | 'topbar.left'
  | 'topbar.center'
  | 'topbar.right'
  | 'statusbar';

export interface PanelProps {
  readonly services: AppServices;
}

/** A dock panel's id is how other packages show it (`ui.showPanel`); the Problems tab is 'problems'. */
export interface PanelDef {
  readonly id: string;
  readonly title: MessageKey;
  readonly icon?: IconName;
  readonly slot: PanelSlot;
  readonly order: number;
  readonly level: ExperienceLevel;
  readonly component: ComponentType<PanelProps>;
  /** Layout presets in which the panel starts collapsed. */
  readonly collapsedIn?: readonly string[];
}

export interface DialogProps<K extends DialogId> {
  readonly services: AppServices;
  readonly props: DialogMap[K]['props'];
  /** Resolve the opener's promise (dismissal resolves null automatically). */
  close(result: DialogMap[K]['result']): void;
}

export interface DialogDef<K extends DialogId> {
  readonly id: K;
  /** modal: centered, blocks; sheet: non-modal side sheet with live ghost preview (generators); popover: anchored. */
  readonly kind: 'modal' | 'sheet' | 'popover';
  /**
   * Renders the WHOLE dialog, frame included (`Dialog` / `Sheet` from src/ui with its title, footer and `onClose` ->
   * `close(null)`; a 'popover' draws its content only, the host draws the anchored surface). The DialogHost only
   * mounts it while the dialog is open, passes `close`, and resolves null if it is unmounted without a result.
   * Plain function type (bivariant enough for typed components, no ComponentClass defaultProps pitfalls).
   */
  readonly component: (p: DialogProps<K>) => ReactNode;
}

/** Any registered dialog definition (union over every DialogId). */
export type AnyDialogDef = { [K in DialogId]: DialogDef<K> }[DialogId];

export interface InspectorSectionProps {
  readonly services: AppServices;
  readonly selection: Selection;
}

export interface InspectorSectionDef {
  readonly id: string;
  /** Lower first. Core sections: note 100, segment 200, key 210, line 300, tempo 400, effect 500, chart 900. */
  readonly order: number;
  readonly level: ExperienceLevel;
  appliesTo(sel: Selection, services: AppServices): boolean;
  readonly component: ComponentType<InspectorSectionProps>;
}

export interface WizardStepProps {
  readonly services: AppServices;
  /** Mutable draft shared by all steps until "Create". */
  readonly draft: NewChartDraft;
  update(patch: Partial<NewChartDraft>): void;
  setCanContinue(ok: boolean): void;
}

/** New-chart wizard draft (DESIGN.md §4.3, §5.1). */
export interface NewChartDraft {
  /** `sha256` (from io.sha256) is the audio key for `dsp.setPcm({ audio })` and the analysis cache; the Song step calls setPcm. */
  readonly music: { readonly bytes: Uint8Array; readonly sha256: AssetId; readonly name: string; readonly mime: string; readonly durationSec: number; readonly converted: boolean } | null;
  readonly illustration: { readonly bytes: Uint8Array; readonly name: string; readonly mime: string; readonly placeholder: boolean } | null;
  readonly name: string;
  readonly composer: string;
  readonly illustrator: string;
  readonly charter: string;
  readonly levelLabel: string;
  readonly difficulty: number;
  readonly bpm: number;
  readonly offsetMs: number;
  readonly timingConfirmed: boolean;
  readonly start: 'oneLine' | 'twoLines' | 'empty' | 'rpe24';
  /** Song-select preview range (defaults: start = min(45, max(0, duration - 16)), end = start + 15, clamped). */
  readonly previewStart: number;
  readonly previewEnd: number;
  /** Background tempo/offset detection started at the Song step, consumed by the Beat step (W2-08). */
  readonly analysis: {
    readonly candidates: readonly TempoCandidate[] | null;
    readonly offset: { readonly offsetMs: number; readonly confidence: number } | null;
  } | null;
  /** Steps the user skipped (each becomes a Problems entry, never a silent default). */
  readonly skipped: readonly WizardStepId[];
}

export type WizardStepId = 'song' | 'picture' | 'details' | 'beat' | 'start';

export interface WizardStepDef {
  readonly id: WizardStepId;
  readonly order: number;
  readonly title: MessageKey;
  readonly skippable: boolean;
  readonly component: ComponentType<WizardStepProps>;
}

export interface SettingDef {
  readonly path: SettingPath;
  readonly title: MessageKey;
  readonly description?: MessageKey;
  readonly level: ExperienceLevel;
  /** Custom editor; default editors exist for boolean/number/enum/string. */
  readonly component?: ComponentType<{ readonly services: AppServices }>;
  readonly kind: 'boolean' | 'number' | 'enum' | 'string' | 'custom';
  readonly options?: ReadonlyArray<{ readonly value: Json; readonly label: MessageKey }>;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly unit?: string;
}

export interface HelpTopicDef {
  readonly id: string;
  readonly title: MessageKey;
  /** Markdown-lite body message (no HTML). */
  readonly body: MessageKey;
  /** "Show me": highlight a panel or a DOM element with data-help-id. */
  readonly showMe?: {
    readonly panel?: string;
    readonly helpId?: string;
    /** Where the "Show me" bubble must not sit (an area to keep clear). */
    readonly avoid?: { readonly panel?: string; readonly helpId?: string };
  };
  readonly level: ExperienceLevel;
}

export interface RouteDef {
  /** Hash routes: '#/', '#/settings', '#/p/:id/:workspace'. */
  readonly path: string;
  readonly component: ComponentType<{ readonly services: AppServices; readonly params: Readonly<Record<string, string>> }>;
}

/**
 * Top-bar / status chips (save state, problems count, level, "changed since export", preview-differs banner).
 * 'banner' items are global: the Library hosts them too (e.g. 'import.queue', the import queue's progress).
 */
export interface StatusItemDef {
  readonly id: string;
  readonly slot: 'topbar.right' | 'statusbar' | 'banner';
  readonly order: number;
  readonly component: ComponentType<{ readonly services: AppServices }>;
}

/** Palette entries for a prefix (`#` lines, `@` time/markers, `?` help topics, `:` settings, `=` queries P1). */
export interface PaletteItem {
  readonly id: string;
  readonly title: MessageRef;
  readonly detail?: MessageRef;
  readonly icon?: IconName;
  run(): void | Promise<void>;
}

export interface PaletteProvider {
  readonly prefix: '#' | '@' | '?' | ':' | '=';
  search(query: string): readonly PaletteItem[] | Promise<readonly PaletteItem[]>;
}

/**
 * "Drop anywhere" (Library and editor): the shell asks handlers by descending priority; the first that accepts
 * handles. Dropped folders are expanded by the shell into their files (recursively, name order, OS clutter such as
 * .DS_Store / __MACOSX / Thumbs.db skipped, at most 10 000 files); each file from a folder carries its path below the
 * drop in `file.webkitRelativePath` ("Resources/12345/chart.json"), exactly like a folder picked with
 * `<input type="file" webkitdirectory>`; loose files have ''. One drop = one `accept` / `handle` call.
 */
export interface FileDropHandler {
  readonly id: string;
  readonly priority: number;
  accept(files: readonly File[]): boolean;
  handle(files: readonly File[]): Promise<void>;
}

/**
 * THE PLUG-IN UNIT. Each feature package adds `src/features/<feature>/<feature>.module.ts(x)` with
 * `export default { id, ... } satisfies FeatureModule`. No shared file needs editing.
 * Checker rules are separate (`src/features/checker/rules/*.rules.ts`, worker-safe) and messages are `*.messages.ts`.
 */
export interface FeatureModule {
  readonly id: string;
  /** Method bivariance lets typed CommandDef<{...}> entries in; the registry calls `run(ctx, args)` with JSON args. */
  readonly commands?: readonly CommandDef[];
  readonly keybindings?: Partial<Readonly<Record<KeymapPresetId, readonly KeyBinding[]>>>;
  readonly panels?: readonly PanelDef[];
  readonly dialogs?: readonly AnyDialogDef[];
  readonly inspectorSections?: readonly InspectorSectionDef[];
  readonly wizardSteps?: readonly WizardStepDef[];
  readonly settings?: readonly SettingDef[];
  /** Factory so column views can close over services (doc, engine, editor store). */
  readonly timelineColumns?: (s: AppServices) => readonly TimelineColumnDef[];
  readonly timelineDecorations?: (s: AppServices) => TimelineDecorationProvider;
  readonly previewOverlays?: (s: AppServices) => readonly PreviewOverlayProvider[];
  readonly helpTopics?: readonly HelpTopicDef[];
  readonly routes?: readonly RouteDef[];
  readonly statusItems?: readonly StatusItemDef[];
  /** Global file drop (W2-09 is the only P0 contributor; the shell hosts the drop zone and asks the handlers). */
  readonly fileDropHandlers?: (s: AppServices) => readonly FileDropHandler[];
  /** Palette prefix providers (`#` lines by W2-07, `?` help by W2-14, `:` settings by W2-13, `@` time by W1-11b). */
  readonly paletteProviders?: (s: AppServices) => readonly PaletteProvider[];
  /** Extra WhenContext atoms (e.g. 'testPlay', 'theater'); names must not collide with BUILTIN_WHEN_ATOMS. */
  readonly whenAtoms?: (s: AppServices) => Readonly<Record<string, () => boolean>>;
  /** Always-mounted overlays of a feature (package-internal dialogs, progress), drawn by the shell above every route. */
  readonly overlays?: readonly OverlayDef[];
  /** Called once at startup (after services exist); may return a disposer. */
  init?(s: AppServices): (() => void) | undefined;
}

/** A feature's always-mounted overlay (FeatureModule.overlays): rendered on every route, in its own error boundary. */
export interface OverlayDef {
  readonly id: string;
  readonly component: ComponentType<{ readonly services: AppServices }>;
}

export interface Registry {
  readonly modules: readonly FeatureModule[];
  command(id: CommandId): CommandDef | undefined;
  commands(): readonly CommandDef[];
  panels(slot: PanelSlot): readonly PanelDef[];
  dialog<K extends DialogId>(id: K): DialogDef<K> | undefined;
  inspectorSections(): readonly InspectorSectionDef[];
  wizardSteps(): readonly WizardStepDef[];
  settings(): readonly SettingDef[];
  timelineColumns(s: AppServices): readonly TimelineColumnDef[];
  timelineDecorations(s: AppServices): readonly TimelineDecorationProvider[];
  previewOverlays(s: AppServices): readonly PreviewOverlayProvider[];
  fileDropHandlers(s: AppServices): readonly FileDropHandler[];
  paletteProviders(s: AppServices): readonly PaletteProvider[];
  /** Built-in plus module-registered atom names (for parseWhen validation). */
  whenAtomNames(): ReadonlySet<string>;
  helpTopics(): readonly HelpTopicDef[];
  routes(): readonly RouteDef[];
  statusItems(slot: StatusItemDef['slot']): readonly StatusItemDef[];
  overlays(): readonly OverlayDef[];
  menu(id: MenuId): ReadonlyArray<{ readonly group: string; readonly commands: readonly CommandDef[] }>;
  /** Run a contracted cross-package command (typed args); returns false when disabled or unknown. */
  run<K extends keyof CommandArgs>(id: K, args: CommandArgs[K], source?: CommandSource): Promise<boolean>;
  /** Run any command by id with JSON args (keymap, palette, menus, checker fixes). */
  runAny(id: CommandId, args?: Json, source?: CommandSource): Promise<boolean>;
}

// ---------------------------------------------------------------------------------------------------------------
// Services available to commands and components
// ---------------------------------------------------------------------------------------------------------------

export interface ToastOptions {
  readonly kind?: 'info' | 'success' | 'warning' | 'error';
  /** Undo link (runs Edit > Undo). */
  readonly undo?: boolean;
  readonly action?: { readonly label: MessageRef; readonly command: CommandId; readonly args?: Json };
  readonly durationMs?: number;
}

/** Handle returned by `UiService.toast`; `dismiss()` closes the toast the call showed (no-op when it is gone). */
export interface ToastHandle {
  dismiss(): void;
}

export interface UiService {
  toast(msg: MessageRef, opts?: ToastOptions): ToastHandle;
  confirm(opts: { readonly title: MessageRef; readonly body: MessageRef; readonly confirm: MessageRef; readonly danger?: boolean }): Promise<boolean>;
  choose<T extends string>(opts: { readonly title: MessageRef; readonly body?: MessageRef; readonly options: ReadonlyArray<{ readonly value: T; readonly label: MessageRef; readonly description?: MessageRef }> }): Promise<T | null>;
  /** Opens a registered dialog; resolves with its typed result (null when dismissed). */
  openDialog<K extends DialogId>(id: K, props: DialogMap[K]['props']): Promise<DialogMap[K]['result']>;
  closeDialog(id: DialogId): void;
  /**
   * Gesture sub-actions shown in the hint bar ("R end hold · Esc cancel · Alt free X"). `chord: ''` is a sentence
   * without a key (the bar shows only the label, e.g. the gizmo's per-axis key rule).
   */
  setGestureHints(items: ReadonlyArray<{ readonly chord: Chord; readonly label: MessageRef }> | null): void;
  showPanel(id: string): void;
  focusRegion(slot: PanelSlot): void;
  /** First-use tip after repeated mouse use of a command that has a shortcut (P§6.6). */
  noteMouseUse(command: CommandId): void;
  openPalette(prefix?: '>' | '#' | '@' | '=' | '?' | ':'): void;
  /**
   * Opens a registry menu as a context menu at a viewport position (canvases and columns that handle their own right
   * click: hit tests, keyboard Menu key). Commands chosen run with source 'context' and `ctx.event` = `event`.
   */
  openContextMenu?(menu: MenuId, x: number, y: number, event?: PointerEvent | null): void;
}

export interface ProblemsService {
  problems(): readonly Problem[];
  counts(): Readonly<Record<'fatal' | 'error' | 'warning' | 'tip', number>>;
  subscribe(cb: () => void): Unsubscribe;
  /** Next/previous problem after the playhead (Alt+] / Alt+[). */
  next(dir: 1 | -1): Problem | null;
  /** Rules available (from *.rules.ts) for the settings page. */
  rules(): readonly Pick<CheckRule, 'id' | 'title' | 'category' | 'severity' | 'level'>[];
  fullCheck(): void;
  /** Runs fix `fixIndex` of a problem (Problems panel "Fix", Ctrl+.); false when the problem is gone or the command is disabled. */
  applyFix(problemId: string, fixIndex: number): Promise<boolean>;
  /** A pass that covers the latest changes (or a `fullCheck`) has not finished yet (the export preflight waits). */
  checking(): boolean;
  /** Problems at an inspector field and/or entity (in-field badges, DESIGN.md §5.10). */
  problemsAt?(q: ProblemQuery): readonly Problem[];
  /** Ready-made badges for a field (severity, message tooltip, click shows the problem in Problems). */
  fieldBadges?(q: ProblemQuery): readonly BadgeProps[];
  /** "Ignore here" (stored in the document, undoable); false when the problem is gone. */
  ignore?(problemId: string): boolean;
  /** Custom shader compile errors of the preview (PH-33); replaces the previous list. */
  reportShaderErrors?(errors: ReadonlyArray<{ readonly asset: AssetId; readonly log: string }>): void;
}

/** Where a problem is: an inspector field path and/or the entities it concerns (ProblemsService.problemsAt). */
export interface ProblemQuery {
  readonly field?: string;
  readonly line?: LineId;
  readonly note?: NoteId;
  readonly key?: KeyId;
  readonly track?: TrackId;
  readonly asset?: AssetId;
  readonly effect?: EffectId;
}

/**
 * Implemented by W2-12a in src/features/checker/problemsService.ts as `export const createProblemsService`.
 * Feeds the analysis worker (init/txn/resync/configure incl. player settings, skin metrics and the reference
 * viewport), keeps the problem list, answers next/previous. Wave-0 stub: INERT (empty, never changes).
 */
export type CreateProblemsService = (deps: {
  readonly analysis: RpcClient<AnalysisWorkerSchema, AnalysisWorkerEvents>;
  readonly getStore: () => DocStore | null;
  readonly settings: SettingsService;
}) => ProblemsService;

export interface ClipboardService {
  readonly payload: ClipboardPayload | null;
  set(p: ClipboardPayload): Promise<void>;
  /** Reads the system clipboard (other tabs/projects); falls back to the internal payload. */
  read(): Promise<ClipboardPayload | null>;
}

export interface NavService {
  openProject(id: ProjectId, workspace?: WorkspaceId): Promise<void>;
  closeProject(): Promise<void>;
  goLibrary(): void;
  goSettings(section?: string): void;
  setWorkspace(ws: WorkspaceId): void;
  /** Re-runs the last route-driven open that failed (the error surface's "Try again"); resolves when it is done. */
  retryRoute(): Promise<void>;
}

export interface AppServices {
  /** Null while no project is open (Library). */
  readonly store: DocStore | null;
  readonly editor: EditorStoreApi;
  readonly engine: Engine | null;
  readonly audio: AudioEngine;
  readonly persist: PersistClient;
  readonly io: RpcClient<IoWorkerSchema, IoWorkerEvents>;
  readonly dsp: RpcClient<AudioDspWorkerSchema, AudioDspWorkerEvents>;
  readonly analysis: RpcClient<AnalysisWorkerSchema, AnalysisWorkerEvents>;
  readonly problems: ProblemsService;
  readonly skins: SkinStore;
  readonly textures: TextureProvider | null;
  /** The chart workspace's timeline host when mounted. */
  readonly timeline: TimelineHostApi | null;
  /** Called by the timeline panel (W2-03) when it mounts/unmounts its host. */
  attachTimeline(host: TimelineHostApi | null): void;
  readonly scheduler: FrameScheduler;
  readonly registry: Registry;
  readonly keymap: KeymapService;
  readonly settings: SettingsService;
  readonly i18n: I18n;
  readonly ui: UiService;
  readonly clipboard: ClipboardService;
  readonly nav: NavService;
  /** Subscribe to service swaps (project open/close replaces store/engine/textures/timeline). */
  onChange(cb: () => void): Unsubscribe;
}
